// @flow
import React, { useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import Icon from '@material-ui/core/Icon';
import classes from './Rating.module.css';

const InputTextField = withStyles({
	root: {
		'& .MuiInput-underline, & .MuiTypography-root': {
			'&:after': {
				borderBottom: '2px solid #bfd730',
			},
		},
		'& label,& .Mui-focused': {
			color: 'black',
		},
		'& label + .MuiInput-formControl': {
			marginTop: 10,
			marginBottom: 10,
		},
	},
})(TextField);

const useStyles = makeStyles((theme) => ({
	header: {
		color: '#2e2e2e',
		textAlign: 'center',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		'& h4': {
			margin: 0,
		},
	},
	button: {
		width: '100%',
		lineHeight: '30px',
		backgroundColor: '#bfd730',
		borderRadius: '5px',
		fontSize: '14px',
		'&:hover': {
			backgroundColor: '#bfd730',
		},
	},
	error: {
		color: '#f44336',
		fontSize: '11px',
		marginTop: '2px',
	},
	checkbox: {
		'&.Mui-checked': {
			color: '#bfd730',
		},
	},

	icon: {
		fontSize: 40,
		'&.selected': {
			color: '#f5a239',
		},
	},
	mainContainer: {
		transform: 'scaleY(0)',
		transition: 'transform 300ms',
		transformOrigin: 'top center',
		height: 0,
		'&.view': {
			height: 'auto',
			transform: 'scaleY(1)',
		},
		'& header': {
			marginBottom: 10,
		},
	},
	reasonContainer: {
		display: 'flex',
		flexDirection: 'column',
		marginLeft: '10px',
	},
	contactMe: {
		marginTop: 10,
		marginBottom: 10,
	},
}));

type Props = {|
	onSubmit: (Object) => void,
|};

const reasons = [
	'Mobile App is not easy to use',
	'Machine was not working',
	'Bad or expired product',
	'Billing issues',
];

const heading = 'Please rate your overall experience';

function Rating({ onSubmit }: Props) {
	const styles = useStyles();
	const [comments, setComments] = useState('');
	const initiateSubmit = useRef(false);
	const [contact, setContact] = useState<boolean>(false);
	const [reasonIndices, setReasonIndices] = useState<Array<boolean>>([]);
	const [rating, setRating] = useState<number>(0);

	const ratingStars = useMemo(() => {
		const stars = [];
		for (let index = 0; index < rating; index++) {
			stars.push(
				<Icon className={`${styles.icon} selected`} data-key={index + 1} key={index}>
					star
				</Icon>
			);
		}

		for (let index = rating; index < 5; index++) {
			stars.push(
				<Icon className={styles.icon} data-key={index + 1} key={index}>
					star_outline
				</Icon>
			);
		}
		return stars;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rating]);

	const handleRatingClick = (event) => {
		setRating(+event.target.dataset.key);
	};

	const handleSubmit = () => {
		const params: Object = {
			rating,
			heading,
			contact: +contact,
			reasons: [],
			comments: '',
		};

		if (rating < 4) {
			params.reasons = [];
			reasonIndices.forEach((element, index) => {
				if (element) {
					params.reasons.push(reasons[index]);
				}
			});
			params.comments = comments.trim();
		}
		onSubmit(params);
	};

	return (
		<section>
			<header className={styles.header}>
				<h4>{heading}</h4>
				<section onClick={handleRatingClick} className={`${classes.iconContainer} ${rating > 0 ? classes.view : ''}`}>
					{ratingStars}
				</section>
			</header>
			<main className={`${styles.mainContainer} ${rating > 0 ? 'view' : ''}`}>
				<section style={{ display: rating > 3 ? 'none' : 'block' }}>
					<header>Please tell us more about the issue</header>
					<main className={styles.reasonContainer}>
						{reasons.map((reason, index) => (
							<FormControlLabel
								key={index}
								control={
									<Checkbox
										classes={{ colorSecondary: styles.checkbox }}
										icon={<CheckBoxOutlineBlankIcon fontSize='small' />}
										checkedIcon={<CheckBoxIcon fontSize='small' />}
										value={!!reasonIndices[index]}
										checked={!!reasonIndices[index]}
										onChange={() =>
											setReasonIndices((state) => {
												state[index] = !state[index];
												return [...state];
											})
										}
									/>
								}
								label={reason}
							/>
						))}
						<InputTextField
							id='comments'
							label='Comments'
							multiline
							rows='3'
							rowsMax='4'
							style={{ width: '100%' }}
							value={comments}
							onChange={(event) => setComments(event.target.value)}
							className={styles.textField}
						/>
						<FormControlLabel
							classes={{ root: styles.contactMe }}
							control={
								<Checkbox
									classes={{ colorSecondary: styles.checkbox }}
									icon={<CheckBoxOutlineBlankIcon fontSize='small' />}
									checkedIcon={<CheckBoxIcon fontSize='small' />}
									value={contact}
									checked={contact}
									onChange={() => setContact(!contact)}
								/>
							}
							label="It's ok to contact me"
						/>
					</main>
				</section>
				<section>
					<Button
						variant='contained'
						disabled={initiateSubmit.current}
						classes={{ contained: styles.button }}
						onClick={handleSubmit}
					>
						Submit
					</Button>
				</section>
			</main>
		</section>
	);
}

Rating.propTypes = {
	onSubmit: PropTypes.func.isRequired,
};

export default React.memo<Props>(Rating);
